import type { Editor } from '@tiptap/react'
import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Per-tab deferred-paint lifecycle (S5) for an editor's staged AI
 * suggestions, factored out of `CollaborativeEditor` so it can be hoisted to a
 * page that renders the editor inside a Liveblocks suspense boundary.
 *
 * Why hoist: the staged-suggestions banner only needs the per-practitioner
 * staging read + this reveal state, NOT the editor DOM. On the visit-note page
 * the editor sits under `<ClientSideSuspense>`, so a banner owned inside the
 * editor cannot show until Liveblocks connects (the ~3s delay). Owning this
 * state — and the banner — at the page level lets the banner paint as soon as
 * the (parallel) staging search settles, while the editor still drives the
 * actual paint via the threaded-down `revealed`/`revealGen`.
 *
 * `revealed` = "the suggestion review apparatus is active in THIS tab". It is
 * latched ONCE, at the load edge (when `isResourceLoaded` first turns true),
 * from whether there was pre-existing pending staging at that instant:
 *   - clean resource (no staging at load) → `revealed = true` → hydration is a
 *     no-op but the reconciler + sweep are active, so a live generation in this
 *     tab paints and its accept/reject/undo all work — and no banner shows.
 *   - resource with pre-existing staging at load → `revealed = false` →
 *     hydration stays dormant and the banner shows instead. "Review suggestions"
 *     flips `revealed` → hydration paints.
 * Latching at the load transition (not deriving live from `hasPersistedPending`)
 * keeps a clean-resource live generation from spuriously raising the banner: once
 * staging appears via the live write, `hasPersistedPending` becomes true, but
 * `revealed` was already latched true at the clean load.
 */
export interface DeferredSuggestionReveal {
  /** Whether the review apparatus (decorations, status bar, hover popover) is active in this tab. */
  revealed: boolean
  /**
   * True from the moment a reveal is requested until the host reports the
   * hydration paint has run. The wait is real and can be seconds: the paint is
   * gated on the collaborative doc having connected AND completed its initial
   * sync, and on the visit-note page the banner is rendered outside that
   * suspense boundary, so "Review suggestions" is clickable while the editor is
   * still a read-only placeholder. Hosts keep the banner mounted through this
   * window so the click has a visible effect instead of blanking the surface.
   */
  revealing: boolean
  /** Bumped on every (re-)reveal so the hydration one-shot re-arms after a Hide. */
  revealGen: number
  /** Reveal + paint the staged suggestions in this tab (banner → review). */
  handleReveal: () => void
  /** Host signal that the hydration paint has run; clears `revealing`. */
  handleRevealSettled: () => void
  /**
   * Live staging painted new proposals into this editor. While the tab is still
   * deferred this runs the same reveal the banner's button runs, so the note
   * never sits half-painted (new proposals visible, pre-existing ones hidden
   * behind the banner). A no-op once revealed.
   */
  handleLiveStaged: () => void
  /** Un-paint the decorations and return to the deferred banner (status bar "Hide"). */
  handleHide: () => void
}

export function useDeferredSuggestionReveal({
  resourceKey,
  isResourceLoaded,
  hasPersistedPending,
  getEditor,
}: {
  /**
   * Identity of the resource this reveal state belongs to (its reference). When
   * a host owns this hook ABOVE the per-resource-keyed editor subtree (the
   * visit-note page, where only the editor remounts on resource switch), the
   * hook itself does not remount between resources — so it must re-latch when
   * `resourceKey` changes, or the prior resource's `revealed` would leak into
   * the next (showing the banner vs auto-painting for the wrong resource). Pass
   * `undefined` for hosts that remount the hook per resource (the latch then
   * simply fires once on the only resource it ever sees).
   */
  resourceKey: string | undefined
  isResourceLoaded: boolean
  hasPersistedPending: boolean
  /**
   * Returns the live editor instance for the Hide path (which tears down painted
   * decorations via `rejectAllAIProposals`). May return null while the editor is
   * still booting; Hide is only reachable once the apparatus is revealed, which
   * only happens after the editor is ready, so a null read is a harmless no-op.
   */
  getEditor: () => Editor | null
}): DeferredSuggestionReveal {
  const [revealed, setRevealed] = useState(false)
  const [revealing, setRevealing] = useState(false)
  const [revealGen, setRevealGen] = useState(0)

  const revealLatchedRef = useRef(false)
  const latchedResourceKeyRef = useRef(resourceKey)
  const hasPersistedPendingRef = useRef(hasPersistedPending)
  hasPersistedPendingRef.current = hasPersistedPending

  // Re-arm the latch when the resource identity changes so a host that outlives
  // the editor across a resource switch starts the next resource's reveal fresh.
  // Reset `revealed` to the safe default (banner-side) during this render too, so
  // the stale prior-resource value can't flash before the effect re-latches —
  // the standard "derive state from a changing prop" pattern.
  if (latchedResourceKeyRef.current !== resourceKey) {
    latchedResourceKeyRef.current = resourceKey
    revealLatchedRef.current = false
    setRevealed(false)
    setRevealing(false)
  }

  /* `resourceKey` is an intentional dependency even though the effect reads it
   * only through the render-time ref reset above: after a resource switch
   * re-arms the latch, the effect must re-run to set the new resource's
   * `revealed` — and `isResourceLoaded` alone may not have changed (resource B
   * can load with the same `true`). Without `resourceKey` here, switching to an
   * already-loaded resource would never re-latch. */
  // resourceKey re-runs the latch on resource switch (see above)
  useEffect(() => {
    if (!isResourceLoaded || revealLatchedRef.current) {
      return
    }
    revealLatchedRef.current = true
    // Active by default; deferred (banner) only when loading into pre-existing
    // staging. Read pending via ref so this latches exactly on the load edge.
    setRevealed(!hasPersistedPendingRef.current)
  }, [isResourceLoaded, resourceKey])

  /* Reveal (paint) the staged suggestions in this tab. Bump `revealGen` so a
   * re-reveal after a Hide re-arms the hydration one-shot and re-paints. */
  const handleReveal = useCallback(() => {
    setRevealGen((g) => g + 1)
    setRevealed(true)
    setRevealing(true)
  }, [])

  /* Cleared by the host once the hydration paint's preconditions are met (the
   * paint itself is synchronous inside that effect), so the banner's loading
   * state covers exactly the collab-sync wait and nothing more. */
  const handleRevealSettled = useCallback(() => {
    setRevealing(false)
  }, [])

  /* Auto-reveal on live staging: the AI chat stages new proposals straight into
   * the editor's decoration plugin, which paints them regardless of `revealed`.
   * On a note that loaded WITH pre-existing staging (`revealed = false`) that
   * leaves the new proposals painted while the old ones stay dormant behind the
   * banner. Painted-ness is all-or-nothing per note, so route through the same
   * `handleReveal` the banner's "Review suggestions" button runs. Read `revealed`
   * through a ref so the callback identity stays stable for the registry
   * registration that carries it. */
  const revealedRef = useRef(revealed)
  revealedRef.current = revealed
  const handleLiveStaged = useCallback(() => {
    if (revealedRef.current) {
      return
    }
    handleReveal()
  }, [handleReveal])

  /* Hide (task #4): tear down the painted decorations via the existing
   * remove-proposals path (decoration-only — writes NO status, mirrors the
   * reconciler/sweep's `rejectAIProposal`), then flip back to the deferred
   * banner. Records stay PENDING — Hide is un-paint, not reject. */
  const handleHide = useCallback(() => {
    getEditor()?.chain().focus().rejectAllAIProposals().run()
    setRevealed(false)
    setRevealing(false)
  }, [getEditor])

  return {
    revealed,
    revealing,
    revealGen,
    handleReveal,
    handleRevealSettled,
    handleLiveStaged,
    handleHide,
  }
}
